import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Palette, Ruler, Users, Building2 } from 'lucide-react';
import { categories, products, subCategories } from '../data/products';
import { useTheme } from '../context/ThemeContext';
import { Helmet } from "react-helmet-async";

<Helmet>
  <title>Premium Uniform Manufacturer in India | Hotel, Hospital & School Uniforms</title>
  <meta name="description" content="Trusted uniform manufacturer providing high-quality hotel, hospital, and school uniforms. Premium fabrics, modern designs, and custom uniform solutions." />
    <meta property="og:title" content="Premium Uniform Manufacturer in India" />
  <meta property="og:description" content="High-quality uniforms for hotels, hospitals, and schools." />
  <meta property="og:type" content="website" />
  <meta name="keywords" content="uniform manufacturer, hotel uniforms, hospital uniforms, school uniforms, custom uniforms India, bulk uniform supplier" />
</Helmet>

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_4a46bb01-e425-48c8-a790-c185a7fd0b57/artifacts/6wdpibd5_logomedusa.jpeg';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export const Home = () => {
  const { isDarkMode } = useTheme();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const featuredProducts = products.slice(0, 8);

  // Get unique subcategories for catalog display
  const hotelSubCats = subCategories.hotel;

  return (
    <div data-testid="home-page" className={isDarkMode ? 'bg-zinc-900' : ''}>
      {/* Hero Section - Logo as Main Background */}
      <section className="min-h-screen relative flex items-center overflow-hidden" data-testid="hero-section">
        {/* Logo as Main Background */}
        <div className="absolute inset-0 z-0 flex items-center justify-center">
          <img
            src={LOGO_URL}
            alt=""
            className="w-full h-full object-contain opacity-[0.08]"
            style={{
              filter: isDarkMode ? 'invert(1) sepia(1) saturate(0.5) hue-rotate(10deg)' : 'sepia(0.3) saturate(0.8)',
              maxWidth: '80%',
              maxHeight: '80%'
            }}
          />
        </div>
        
        {/* Yellowish tone overlay */}
        <div className={`absolute inset-0 z-0 ${isDarkMode ? 'bg-zinc-900/90' : 'bg-[#fdfbf5]/85'}`} />

        <div className="container-custom relative z-10 py-32">
          <div className="max-w-2xl mx-auto text-center">
            <motion.p
              className={`section-subtitle ${isDarkMode ? 'text-gray-400' : ''}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Premium Uniform Designs
            </motion.p>
            
            <motion.h1
              className={`text-4xl sm:text-5xl lg:text-6xl mb-6 ${isDarkMode ? 'text-white' : ''}`}
              style={{ fontFamily: 'Bodoni Moda, serif', lineHeight: 1.1 }}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Elegance in{' '}
              <span className="italic">Uniformity</span>
            </motion.h1>

            <motion.div
              className="gold-line mx-auto mb-8"
              initial={{ width: 0 }}
              animate={{ width: 60 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            />

            <motion.p
              className={`text-base md:text-lg mb-10 leading-relaxed max-w-lg mx-auto ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              We design premium uniforms for hospitality and healthcare professionals. 
              Every design customized as per your measurement and selected colour.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Link to="/products" data-testid="hero-explore-btn">
                <button className="btn-primary flex items-center gap-3">
                  Explore Designs
                  <ArrowRight size={16} />
                </button>
              </Link>
              <Link to="/contact" data-testid="hero-quote-btn">
                <button className={`btn-secondary ${isDarkMode ? 'border-white text-white hover:bg-white hover:text-black' : ''}`}>
                  Get Custom Quote
                </button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Catalog Section - Product Categories on Home Page */}
      <section className={`py-20 md:py-32 ${isDarkMode ? 'bg-zinc-800' : 'bg-white'}`} data-testid="catalog-section">
        <div className="container-custom">
          <motion.div
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <p className={`section-subtitle ${isDarkMode ? 'text-gray-400' : ''}`}>Our Catalogue</p>
            <h2 className={`section-title ${isDarkMode ? 'text-white' : ''}`}>Design Categories</h2>
            <p className={`mt-4 max-w-2xl mx-auto ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Browse our complete range of uniform designs. Every piece crafted as per your measurement and colour preference.
            </p>
          </motion.div>

          {/* Category Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
            {hotelSubCats.map((subCat, index) => (
              <motion.div
                key={subCat.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
              >
                <Link
                  to={`/products?category=hotel&sub=${subCat.id}`}
                  className={`block p-6 text-center border transition-all hover:border-[#D4AF37] group ${
                    isDarkMode ? 'border-zinc-700 bg-zinc-900 hover:bg-zinc-800' : 'border-gray-100 bg-[#f9fafb] hover:bg-white'
                  }`}
                  data-testid={`catalog-${subCat.id}`}
                >
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                    <span className="text-[#D4AF37] text-lg font-medium" style={{ fontFamily: 'Bodoni Moda, serif' }}>
                      {subCat.name.charAt(0)}
                    </span>
                  </div>
                  <h3 
                    className={`text-sm font-medium group-hover:text-[#D4AF37] transition-colors ${isDarkMode ? 'text-white' : ''}`}
                  >
                    {subCat.name}
                  </h3>
                  <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    View Designs →
                  </p>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Hospital Categories */}
          <motion.div
            className="mt-12 text-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <h3 className={`text-lg mb-6 ${isDarkMode ? 'text-white' : ''}`} style={{ fontFamily: 'Bodoni Moda, serif' }}>
              Healthcare Uniforms
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              {subCategories.hospital.map((subCat) => (
                <Link
                  key={subCat.id}
                  to={`/products?category=hospital&sub=${subCat.id}`}
                  className={`px-6 py-3 border transition-all hover:border-[#D4AF37] hover:text-[#D4AF37] ${
                    isDarkMode ? 'border-zinc-700 text-white' : 'border-gray-200'
                  }`}
                  data-testid={`catalog-hospital-${subCat.id}`}
                >
                  {subCat.name}
                </Link>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Customization Banner */}
      <section className={`py-16 md:py-20 ${isDarkMode ? 'bg-zinc-900' : 'bg-[#f9fafb]'}`} data-testid="customization-section">
        <div className="container-custom">
          <motion.div
            className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                <Ruler className="text-[#D4AF37]" size={28} />
              </div>
              <div>
                <h3 className={`text-lg font-medium ${isDarkMode ? 'text-white' : ''}`}>As Per Your Measurement</h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Customized to fit perfectly</p>
              </div>
            </div>

            <div className={`hidden md:block w-px h-12 ${isDarkMode ? 'bg-zinc-700' : 'bg-gray-200'}`} />

            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                <Palette className="text-[#D4AF37]" size={28} />
              </div>
              <div>
                <h3 className={`text-lg font-medium ${isDarkMode ? 'text-white' : ''}`}>As Per Your Selected Colour</h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Choose your preferred colours</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Designs */}
      <section className={`py-20 md:py-32 ${isDarkMode ? 'bg-zinc-800' : 'bg-white'}`} data-testid="featured-section">
        <div className="container-custom">
          <motion.div
            className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <div>
              <p className={`section-subtitle ${isDarkMode ? 'text-gray-400' : ''}`}>Design Gallery</p>
              <h2 className={`section-title ${isDarkMode ? 'text-white' : ''}`}>Featured Designs</h2>
            </div>
            <Link 
              to="/products" 
              className={`text-xs uppercase tracking-wider flex items-center gap-2 hover:text-[#D4AF37] transition-colors ${isDarkMode ? 'text-white' : ''}`}
              data-testid="view-all-products"
            >
              View All Designs
              <ArrowRight size={14} />
            </Link>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {featuredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
              >
                <Link 
                  to={`/products/${product.id}`}
                  className="product-card block group"
                  data-testid={`product-card-${product.id}`}
                >
                  <div className={`overflow-hidden aspect-[3/4] ${isDarkMode ? 'bg-zinc-700' : 'bg-gray-100'}`}>
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className={`p-4 ${isDarkMode ? 'bg-zinc-900' : 'bg-white'}`}>
                    <p className={`text-xs uppercase tracking-wider mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {subCategories.hotel.find(s => s.id === product.subCategory)?.name || 
                       subCategories.hospital.find(s => s.id === product.subCategory)?.name}
                    </p>
                    <h3 
                      className={`text-sm md:text-base group-hover:text-[#D4AF37] transition-colors ${isDarkMode ? 'text-white' : ''}`}
                      style={{ fontFamily: 'Bodoni Moda, serif' }}
                    >
                      {product.name}
                    </h3>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us - Marketing Focus */}
      <section className={`py-20 md:py-32 ${isDarkMode ? 'bg-zinc-900' : 'bg-[#0a0a0a]'} text-white`} data-testid="why-us-section">
        <div className="container-custom">
          <motion.div
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <p className="section-subtitle text-gray-400">The MALABIStheistic Way</p>
            <h2 className="section-title text-white">We Deal in Customization</h2>
            <p className="mt-4 text-gray-400 max-w-2xl mx-auto">
              Our goal is to showcase premium designs. Every design is customized as per your measurement and colour preference.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Palette className="w-8 h-8" />,
                title: 'Design Selection',
                description: 'Choose from our wide range of premium uniform designs'
              },
              {
                icon: <Ruler className="w-8 h-8" />,
                title: 'Your Measurements',
                description: 'Every piece tailored as per your exact measurements'
              },
              {
                icon: <Users className="w-8 h-8" />,
                title: 'Your Colours',
                description: 'Select your preferred colours for the design'
              },
              {
                icon: <Building2 className="w-8 h-8" />,
                title: 'Bulk Orders',
                description: 'Perfect for hotels, hospitals and institutions'
              }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                className="feature-box border-gray-800 hover:border-[#D4AF37] text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="text-[#D4AF37] mb-4 flex justify-center">
                  {feature.icon}
                </div>
                <h3 
                  className="text-lg mb-3"
                  style={{ fontFamily: 'Bodoni Moda, serif' }}
                >
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-20 md:py-32 ${isDarkMode ? 'bg-zinc-800' : 'bg-white'}`} data-testid="cta-section">
        <div className="container-custom">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <p className={`section-subtitle ${isDarkMode ? 'text-gray-400' : ''}`}>Ready to Get Started?</p>
            <h2 className={`section-title mb-8 ${isDarkMode ? 'text-white' : ''}`}>
              Let's Design Your{' '}
              <span className="italic">Perfect Uniform</span>
            </h2>
            <p className={`mb-10 max-w-xl mx-auto ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Connect with us to discuss your design requirements. 
              We customize every design as per your measurement and colour choice.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://wa.me/916398130510?text=Hello%20MALABISthestic!%20I%20am%20interested%20in%20your%20uniform%20designs."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-whatsapp"
              >
                <button className="btn-primary flex items-center gap-3">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Chat on WhatsApp
                </button>
              </a>
              <Link to="/contact" data-testid="cta-contact">
                <button className={`btn-secondary ${isDarkMode ? 'border-white text-white hover:bg-white hover:text-black' : ''}`}>
                  Send Inquiry
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
